import { NextImage, noForwardProps, useAccountContext } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import BlurEffect from '@src/components/share/generic/BlurEffect'
import Countdown from '@src/components/share/generic/Countdown'
import { useVipPassContext } from '@src/contexts/battle/demo/VipPassContext'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

const MainContainer = styled(Box)`
  width: 100%;

  background-color: #000000cc;
  border-radius: 12px;
`

const KeysInfoContainer = styled(Box)`
  width: 100%;
  padding: 40px 20px 0px 20px;

  display: flex;
  flex-direction: row;
  justify-content: space-around;
  align-items: center;
  flex-wrap: wrap;
`

const KeyInfoContainer = styled(Box)`
  width: 200px;
  height: 120px;

  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
`

const KeyAmount = styled(Box, noForwardProps)<{ __none?: boolean }>`
  width: 80%;

  border-radius: 10px;
  background-color: #000000;
  padding: 10px 20px;

  text-align: end;
  font-size: 18px;
  font-family: var(--font-Inter);

  position: relative;

  color: ${props => props.__none && '#FF0000'};
`

const KeyImage = styled(NextImage)`
  position: absolute;

  aspect-ratio: 0.5;

  width: 60px;

  top: 50%;
  transform: translateY(-50%);
  left: -20px;
`

const KeyName = styled(Typography)`
  margin-top: 10px;

  font-size: 15px;
  line-height: 1;
  color: #ffffff99;
`

const CountDownContainer = styled(Box)`
  width: 100%;
  height: fit-content;
  background-color: #000000;

  border-radius: 0px 0px 12px 12px;

  display: flex;
  flex-direction: column;
  justify-content: space-evenly;
  align-items: center;
  gap: 12px;
  padding: 16px;
`

const CountDownTitle = styled(Typography)``

const CountDownBox = styled(Box)`
  display: flex;
  flex-direction: row;

  .Custom-Node-Background {
    border: 2px solid #a9a9a980;
    height: 80px;

    ${props => props.theme.breakpoints.down('res1024')} {
      width: 80px;
    }
  }

  .Custom-Node-Value {
    font-size: 42px;
    line-height: 1;
  }

  .Custom-Node-Label {
    font-size: 16px;
    color: #ffffff99;
  }

  ${props => props.theme.breakpoints.up('res1024')} {
    gap: 20px;
  }

  ${props => props.theme.breakpoints.down('res1024')} {
    gap: 10px;
  }
`

const CountDown = styled(Countdown)``

const BlurText = styled(Typography)`
  font-size: 24px;
  font-weight: 700;
`

const KeyInfo = ({ name, value, image }: KeyInfoProps) => {
  return (
    <KeyInfoContainer>
      <KeyAmount __none={value === 0}>
        <KeyImage src={image} />
        {value}
      </KeyAmount>
      <KeyName>{name}</KeyName>
    </KeyInfoContainer>
  )
}

const VipPassInfoSection = () => {
  const { isLoggedIn } = useAccountContext()

  const { t } = useTranslation()

  const { lootBoxData, currentBattlePass } = useVipPassContext()

  const [countDownEnded, setCountDownEnded] = useState(false)

  const endDate = useMemo(() => {
    return currentBattlePass?.endAt || ''
  }, [currentBattlePass])

  const handleCountDownEnd = () => {
    if (R.isNotEmpty(endDate)) {
      setCountDownEnded(true)
    }
  }

  return (
    <MainContainer>
      <BlurEffect
        active={!isLoggedIn}
        text={<BlurText>{t('vip_pass.info.blur_text')}</BlurText>}
      >
        <KeysInfoContainer>
          {RA.mapIndexed((item, index) => {
            return (
              <KeyInfo
                key={index}
                name={item.keyName}
                value={item.keyCount}
                image={item.keyImage}
              />
            )
          }, lootBoxData || [])}
        </KeysInfoContainer>

        <CountDownContainer>
          <CountDownTitle>
            {countDownEnded
              ? t('vip_pass.info.count_down.title_end')
              : t('vip_pass.info.count_down.title')}
          </CountDownTitle>
          {isLoggedIn && (
            <CountDownBox>
              <CountDown targetDate={endDate} onComplete={handleCountDownEnd} />
            </CountDownBox>
          )}
        </CountDownContainer>
      </BlurEffect>
    </MainContainer>
  )
}

type KeyInfoProps = {
  name: string
  value: number
  image: string
}

export default VipPassInfoSection
